import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card } from '@material-ui/core';
import {
  BackdropCircular,
  Breadcrumb
} from 'components';
import {
  getCustomersImportQueueTasksApi,
  getExistingCustomersByEmailsApi,
  getStoreCustomerGroupsApi
} from 'api';
import { csvFieldsValidate } from './utils/csvFieldsValidate';
import FilePreviewTable from './FilePreviewTable';
import UploadZone from './UploadZone';
import DownloadTemplateButton from './DownloadTemplateButton';
import ValidationResults from './ValidationResults';
import GroupsSettings from './GroupsSettings';
import CustomersImportProgress from './CustomersImportProgress';


const requiredHeadersStore = 'First Name,Last Name,Email,Phone For Login,Gender,Date Of Birth,Group';
const requiredHeadersCustomer3 = 'First Name,Last Name,Email,Phone For Login,Gender,Date Of Birth,Company/Practice Name,Group';
//TODO: enable limit after testing
// const MAX_ROWS = 101; //must be +1 to include headers

const CustomersImport = () => {
  const dispatch = useDispatch();
  const [fileData, setFileData] = useState({
    headers: [],
    data: [],
    name: ''
  });
  const {data, headers} = fileData;
  const [headersError, setHeadersError] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [existingCustomersEmails, setExistingCustomersEmails] = useState(new Set());
  const [showValidationScreen, setShowValidationScreen] = useState(false);
  const [showGroupsSettings, setShowGroupsSettings] = useState(false);

  const {
    profile,
    userStoreData: {storeCode, quoteProStoreId}
  } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';
  const requiredHeaders = isCustomer3 ? requiredHeadersCustomer3 : requiredHeadersStore;

  const {
    customersImportQueue,
    loadingCustomersQueue,
    loadingExistingCustomersByEmails,
    customerGroups
  } = useSelector((state: IReduxState) => state.customers);

  const userStore = profile?.stores?.[0];
  const storeViewCode = userStore?.storeCode;
  const isImportInProgress = !!customersImportQueue.length;

  useEffect(() => {
    getCustomersImportQueueTasksApi({dispatch, storeViewCode});

    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  const validateFileData = async () => {
    const emailIndex = headers.indexOf('Email');
    const uniqueEmails = [...new Set(
      data.map((row) => row[emailIndex]?.trim().toLowerCase()).filter(Boolean)
    )];

    const existingCustomers = await getExistingCustomersByEmailsApi({dispatch, emails: uniqueEmails});
    const existingCustomersByEmail = new Map(
      existingCustomers.map((customer) => [customer.email?.toLowerCase(), customer])
    );

    const validationResults = csvFieldsValidate(
      data,
      headers,
      isCustomer3,
      quoteProStoreId,
      customerGroups,
      existingCustomersByEmail
    );
    setValidationResults(validationResults);
    setExistingCustomersEmails(new Set(existingCustomersByEmail.keys()));

    setShowValidationScreen(true);
  };

  const resetFileData = () => {
    setFileData({
      headers: [],
      data: [],
      name: ''
    });
    setValidationResults(null);
    setExistingCustomersEmails(new Set());
    setShowValidationScreen(false);
    setShowGroupsSettings(false);
  };

  const handleShowNextData = () => {
    setShowValidationScreen(false);
    setShowGroupsSettings(true);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Import Customers' }
          ]}
        />
      </div>
      <Card elevation={0} className="mx-10 p-8 overflow-unset text-center">
        {headers.length ?
          <FilePreviewTable
            fileData={fileData}
            requiredHeaders={requiredHeaders}
            resetFileData={resetFileData}
            setHeadersError={setHeadersError}
            validationResults={validationResults}
            existingCustomersEmails={existingCustomersEmails}
          />
          :
          <>
            <UploadZone
              setFileData={setFileData}
              setHeadersError={setHeadersError}
              isDisabled={isImportInProgress}
              // maxRows={MAX_ROWS}
            />
            <DownloadTemplateButton
              headers={requiredHeaders}
            />
          </>
        }
      </Card>
      {showValidationScreen && !showGroupsSettings &&
        <ValidationResults
          results={validationResults}
          data={data}
          resetFileData={resetFileData}
          proceedNext={handleShowNextData}
          customerGroups={customerGroups}
          existingCustomersEmails={existingCustomersEmails}
          emailIndex={headers.indexOf('Email')}
        />
      }
      {!showValidationScreen && showGroupsSettings &&
        <GroupsSettings
          fileData={fileData}
          resetFileData={resetFileData}
          validationResults={validationResults}
          customerGroups={customerGroups}
        />
      }
      {!showValidationScreen && !showGroupsSettings && !isImportInProgress &&
        <div className="flex justify-center p-5">
          <Button
            color="primary"
            variant="contained"
            className="uppercase"
            disabled={!data.length || headersError}
            onClick={validateFileData}
          >
            Validate Data
          </Button>
        </div>
      }
      {!validationResults && !showGroupsSettings && isImportInProgress &&
        <CustomersImportProgress
          headers={requiredHeaders}
          customerGroups={customerGroups}
          isCustomer3={isCustomer3}
        />
      }
      <BackdropCircular
        loading={loadingExistingCustomersByEmails || loadingCustomersQueue}
        title={loadingCustomersQueue ? 'Preparing data for import...' : ''}
      />
    </div>
  );
};

export default CustomersImport;
